import classNames from 'classnames';
import { useState } from 'react';
import { useFormInputState } from '../../hooks';
import { functions } from 'firebaseSDK';
import { useTenancy } from '../../state';
import Button from '../Button';
import ErrorMessage from '../ErrorMessage';
import FlexContainer from '../FlexContainer';
import { Input, TitledBlock } from '../index';
import PlaidPayment from '../PlaidPayment';
import SupportBlock from '../SupportBlock';
import Txt from '../Txt';
import { PaymentSummary } from './components';
import styles from './PaymentFlow.module.scss';

const paymentsTypeDataMap = {
  tenantDeposit: {
    amountLabel: 'How much of the deposit are you paying?',
    frequencyLabel: 'Single payment',
    referenceField: 'depositReference',
    amountField: 'depositAmount',
    summaryTitle: 'Deposit to pay',
  },
  tenantRent: {
    amountLabel: 'How much of the rent are you paying?',
    frequencyLabel: 'Monthly payment',
    referenceField: 'rentReference',
    amountField: 'rentAmount',
    summaryTitle: 'Rent to pay',
  },
};

/**
 * PaymentFlow
 *
 * @return {JSX.Element}
 */
const PaymentFlow = ({ className, paymentType, onSuccess }) => {
  const tenancy = useTenancy();
  const [isConfirm, setIsConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');

  const paymentVariant = paymentsTypeDataMap[paymentType];

  const fullAmount = tenancy.data.payments[paymentVariant.amountField];
  const perTenantAmount = fullAmount / tenancy.data.tenantCount;

  const [state, handleChange, setKeyValue] = useFormInputState(() => ({
    amount: fullAmount,
  }));

  const handleSetPerTenantAmount = () => setKeyValue('amount', perTenantAmount);
  const handleSetFullAmount = () => setKeyValue('amount', fullAmount);

  const handleResult = (error, result) => {
    console.log('Result: ', error, result);
    setToken('');
    if (error) {
      setError(error.toString());
    } else {
      if (onSuccess) {
        onSuccess();
      } else {
        setIsSuccess(true);
      }
    }
  };

  // const handleBackFromConfirm = () => setIsConfirm(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isConfirm) {
      setIsConfirm(true);
      return;
    }

    console.log('Submit', state);
    setIsLoading(true);
    setError('');
    functions.plaid
      .createLinkToken({
        tenancyUid: tenancy.id,
        paymentType: paymentType,
        amount: state.amount,
      })
      .then((r) => {
        const linkToken = r.data?.linkToken;
        console.log(r);
        if (linkToken) {
          setToken(linkToken);
          setIsLoading(false);
        } else {
          throw new Error('Something went wrong');
        }
      })
      .catch((e) => {
        setIsLoading(false);
        setError(e.message || 'Unknown error');
        console.warn(e);
      });
  };

  const isDisabled = isLoading || token;

  return (
    <FlexContainer
      className={classNames(
        styles.PaymentFlow,
        className,
        PaymentFlow.displayName
      )}
      column
    >
      {isSuccess ? (
        <Txt regular>Payment was successful</Txt>
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <FlexContainer column gap flex>
              {isConfirm ? (
                <PaymentSummary
                  amount={state.amount}
                  reference={
                    tenancy.data.payments[paymentVariant.referenceField]
                  }
                  frequency={paymentVariant.frequencyLabel}
                  title={paymentVariant.summaryTitle}
                />
              ) : (
                <TitledBlock title={paymentVariant.amountLabel}>
                  <FlexContainer column gap>
                    <Input
                      // className={styles.InputNumber}
                      pre="£"
                      fullWidth
                      type="number"
                      min="10"
                      max={fullAmount}
                      step="0.01"
                      required
                      name="amount"
                      value={state.amount || ''}
                      onChange={handleChange}
                      disabled={isDisabled}
                      reverse
                    />
                    <FlexContainer gap>
                      <Button
                        flex
                        reverse={state.amount === perTenantAmount}
                        onClick={handleSetPerTenantAmount}
                        disabled={isDisabled}
                      >
                        Per tenant share
                      </Button>
                      <Button
                        flex
                        reverse={state.amount === fullAmount}
                        onClick={handleSetFullAmount}
                        disabled={isDisabled}
                      >
                        All
                      </Button>
                    </FlexContainer>
                  </FlexContainer>
                </TitledBlock>
              )}
              {error ? (
                <ErrorMessage description={error} title="" small />
              ) : null}
              <Button success type="submit" disabled={isDisabled}>
                {isConfirm ? 'Pay' : 'Continue'}
              </Button>
              <SupportBlock noMargin />
            </FlexContainer>
          </form>
          {token ? (
            <PlaidPayment token={token} onResult={handleResult} />
          ) : null}
        </>
      )}
    </FlexContainer>
  );
};

PaymentFlow.displayName = 'PaymentFlow';

export default PaymentFlow;
